import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LOGO = 'https://storage.readdy-site.link/project_files/6c0e61d0-63fd-4d52-9546-5c5e5644f171/5cf4cd26-7f99-44fb-baa6-dacbf1647c05_Intermountain-West-Contractors-west-jordan-utah-cutout.png?v=b85e9b75193f3d9c691bbaaa9462160c';

const NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'Roof Replacement', href: '/services/roof-replacement' },
  { label: 'Roof Repair', href: '/services/roof-repair' },
  { label: 'Commercial', href: '/services/commercial' },
  { label: 'Storm Damage', href: '/services/storm-damage' },
  { label: 'About', href: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    document.body.style.overflow = 'unset';
  }, [location.pathname]);

  const toggleMenu = () => {
    const next = !mobileOpen;
    setMobileOpen(next);
    document.body.style.overflow = next ? 'hidden' : 'unset';
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-charcoal-dark shadow-2xl'
            : 'bg-gradient-to-b from-charcoal/80 to-transparent backdrop-blur-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 cursor-pointer">
            <img src={LOGO} alt="IWC Roofing Utah" className="h-16 w-auto object-contain brightness-0 invert" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-body text-xs font-600 uppercase tracking-wider px-3 py-2 rounded transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  location.pathname === link.href
                    ? 'text-brand-orange bg-white/5'
                    : 'text-white/75 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+18015550199"
              className="flex items-center gap-2 border border-white/25 hover:border-white/60 text-white px-4 py-2.5 rounded-md font-body text-sm font-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-brand-orange text-base"></i>
              (801) 555-0199
            </a>
            <Link
              to="/#estimate"
              className="bg-brand-orange hover:bg-brand-orange-dark text-white px-6 py-2.5 rounded-md font-heading font-700 text-sm uppercase tracking-widest transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Free Estimate
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={toggleMenu}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-2xl transition-transform ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>
      </header>

      {/* Mobile Fullscreen Menu */}
      <div
        className={`fixed inset-0 z-40 bg-charcoal-dark flex flex-col transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-6 h-24 border-b border-white/10">
          <img src={LOGO} alt="IWC Roofing Utah" className="h-14 w-auto object-contain brightness-0 invert" />
          <button onClick={toggleMenu} className="w-10 h-10 flex items-center justify-center text-white cursor-pointer">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-8 space-y-2">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="flex items-center justify-between py-4 border-b border-white/10 cursor-pointer group"
            >
              <span className="font-heading font-800 text-white text-2xl uppercase tracking-wide group-hover:text-brand-orange transition-colors">
                {link.label}
              </span>
              <i className="ri-arrow-right-s-line text-white/40 text-2xl group-hover:text-brand-orange transition-colors"></i>
            </Link>
          ))}
        </div>
        <div className="px-6 py-6 border-t border-white/10 space-y-3">
          <a
            href="tel:+18015550199"
            className="flex items-center justify-center gap-3 w-full border border-white/25 text-white py-4 rounded-md font-heading font-700 text-base uppercase tracking-wide cursor-pointer"
          >
            <i className="ri-phone-fill text-brand-orange text-xl"></i>
            (801) 555-0199
          </a>
          <Link
            to="/#estimate"
            className="flex items-center justify-center w-full bg-brand-orange text-white py-4 rounded-md font-heading font-700 text-base uppercase tracking-wide cursor-pointer"
          >
            Get Free Estimate
          </Link>
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 flex border-t border-white/10">
        <a
          href="tel:+18015550199"
          className="flex-1 flex items-center justify-center gap-2 bg-charcoal-dark py-4 text-white font-heading font-700 text-sm uppercase tracking-wide cursor-pointer"
        >
          <i className="ri-phone-fill text-brand-orange text-lg"></i>
          Call Now
        </a>
        <Link
          to="/#estimate"
          className="flex-1 flex items-center justify-center gap-2 bg-brand-orange py-4 text-white font-heading font-700 text-sm uppercase tracking-wide cursor-pointer"
        >
          <i className="ri-calendar-check-fill text-lg"></i>
          Free Estimate
        </Link>
      </div>
    </>
  );
}
